/**
 * L1 Primitives — Text · Button · Icon · Image · Badge · Monogram · Video ·
 * Stack · Overlay · Grid · Surface · CaptureButton(spec).
 *
 * The value gauge (`Gauge`, bar · segments · ring) lives in `viz.jsx`, which is
 * where the drawing they share belongs.
 *
 * The cross-runtime consistency root. Every component takes the kit's uniform
 * node bag: `{ nodeId, node, variant, props, data, ctx, emit, slots,
 * childItems }` — `props` are already resolved (interp + declared data props),
 * `data` is the resolved `bind`, `emit(signal, payload)` fires a registry
 * signal into the blueprint's connections.
 */
import { Children, useEffect, useRef, useState } from 'react';
import {
  TYPE_STYLE, toneColor, toneSoft, gapValue, resetButton, hitTarget, ratioValue,
  prefersReducedMotion, longPressHandlers, initialsOf, hashKey, MONO_HUES,
} from './ui';
import { DynamicIcon } from 'lucide-react/dynamic';
import { num } from '../paths';
import { buildTokenStyle } from '../tokens';
import { isLucideIconName, lucideIconName } from './lucideIcons';
import { SpecOnly } from './Unsupported';

/** A bound value as ONE run of text — a list reads as a sentence, not JSON. */
function asText(value) {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value
      .map((v) => (v && typeof v === 'object' ? (v.text ?? v.value ?? v.label ?? '') : v))
      .filter((v) => v != null && v !== '')
      .join(', ');
  }
  if (typeof value === 'object') return String(value.text ?? value.value ?? value.label ?? '');
  return String(value);
}

/**
 * `preserveNewlines` is the one thing a long-form body needs that a heading
 * must not have: model-written prose arrives with real `\n`s, and collapsing
 * them turns a five-paragraph post into a wall.
 */
// `maxLines` (app 0.10.2+): ellipsis past N lines; `min-width:0` lets the run give way in a row.
function clampStyle(lines) {
  if (lines === 1) return { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
  return { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines, overflow: 'hidden', minWidth: 0 };
}

export function Text({ variant = 'body', props = {}, data }) {
  const value = props.value !== undefined ? props.value : data;
  const style = TYPE_STYLE[variant] || TYPE_STYLE.body;
  const lines = Math.floor(Number(props.maxLines));
  return (
    <div style={{
      ...style,
      color: toneColor(props.tone),
      textAlign: props.align === 'start' ? 'left' : props.align === 'end' ? 'right' : props.align,
      whiteSpace: props.preserveNewlines ? 'pre-wrap' : undefined,
      textDecoration: props.strikethrough ? 'line-through' : undefined,
      margin: 0,
      ...(lines >= 1 ? clampStyle(lines) : null),
    }}>
      {Array.isArray(props.spans) && props.spans.length
        // One line, two weights: a lead-in that is part of the sentence, not a
        // heading over it — the only reason a paragraph needs runs at all.
        ? props.spans.map((sp, i) => (
          <span key={i} style={{
            fontWeight: sp.bold ? 700 : undefined,
            color: sp.tone ? toneColor(sp.tone) : undefined,
          }}>{asText(sp.value)}</span>
        ))
        : asText(value)}
    </div>
  );
}

const BTN_VARIANT = {
  primary: { background: 'var(--accent)', color: 'var(--accent-ink)', border: '1px solid transparent' },
  secondary: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--line)' },
  ghost: { background: 'transparent', color: 'var(--accent)', border: '1px solid transparent' },
  // INK is the accent's quiet twin: the page's own text colour as the fill,
  // for a CTA that must not spend the brand colour on every row.
  ink: { background: 'var(--text)', color: 'var(--surface)', border: '1px solid transparent' },
  destructive: { background: 'var(--bad-soft)', color: 'var(--bad)', border: '1px solid transparent' },
  // the ink-on-surface pill (a page's ONE main action drawn in the text colour, not the accent)
  contrast: { background: 'var(--text)', color: 'var(--surface)', border: '1px solid transparent' },
};

const BTN_SIZE = {
  small: { padding: '8px 12px', fontSize: 'var(--t-sm)', minHeight: 36 },
  medium: { padding: '11px 16px', fontSize: 'var(--t-md)', minHeight: 44 },
  large: { padding: '14px 20px', fontSize: 'var(--t-lg)', minHeight: 52 },
};

/**
 * 把蓝图的声明式按钮可用态归约为一个布尔值。
 *
 * `all` / `any` / `not` 只组合已解析的事实，避免页面维护与真实输入脱节的额外 flag。
 */
function buttonEnabled(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (Array.isArray(value.all)) return value.all.every(buttonEnabled);
    if (Array.isArray(value.any)) return value.any.some(buttonEnabled);
    if (Object.prototype.hasOwnProperty.call(value, 'not')) return !buttonEnabled(value.not);
  }
  return Boolean(value);
}

/**
 * SEALED: 44pt hit target, a11y name, focus ring, single-flight +
 * double-submit guard. The button emits a semantic `activated` signal — it
 * NEVER touches the host bridge itself; the blueprint's connection decides
 * where that goes.
 */
export function Button({ variant = 'secondary', props = {}, emit, slots }) {
  const [busy, setBusy] = useState(false);
  const guard = useRef(0);
  const flight = useRef(null);
  const enabled = props.enabled === undefined ? true : buttonEnabled(props.enabled);
  const loading = Boolean(props.loading);
  const size = BTN_SIZE[props.size] || BTN_SIZE.medium;

  // §5 immediate-ui-response: the interpreter delivers the universal
  // `submitStatus{submitting}` slot to a button wired to a latency-bearing
  // session-open host action (openMainSession / openSession) the frame it is tapped
  // — the SAME slot the form-submit spinner reads (inputs.jsx) — and clears it on
  // the host's ack or a fallback timeout. So a host-action button acknowledges
  // the tap immediately instead of sitting dead while the capture sheet opens.
  // Rendered exactly like the declared `loading` state: spinner in the icon slot,
  // label kept, aria-busy, and inert while in flight.
  const submitting = Boolean(slots?.submitStatus?.payload?.submitting);
  const showSpinner = loading || submitting;

  // The single-flight window must not outlive the button: a page that
  // navigates away mid-press would otherwise wake a component that is gone.
  useEffect(() => () => { if (flight.current) clearTimeout(flight.current); }, []);

  const onClick = () => {
    const now = Date.now();
    if (!enabled || loading || submitting || busy || now - guard.current < 400) return; // double-submit guard
    guard.current = now;
    setBusy(true);
    emit?.('activated', { action: props.action || props.label || null });
    flight.current = setTimeout(() => setBusy(false), 350); // single-flight window
  };

  return (
    <button
      type="button"
      disabled={!enabled || loading || submitting}
      aria-busy={showSpinner || undefined}
      aria-label={props.label || props.action}
      onClick={onClick}
      onContextMenu={(e) => { e.preventDefault(); emit?.('longPressed', {}); }}
      style={{
        ...resetButton,
        ...BTN_VARIANT[variant] || BTN_VARIANT.secondary,
        ...size,
        ...hitTarget,
        ...(props.tone ? { color: toneColor(props.tone) } : null),
        borderRadius: 'var(--r-pill)',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: props.block === false ? 'auto' : '100%',
        // A pill sized by its label alone reads as a dot on a one-word CTA;
        // `minWidth` is how a row keeps every button the same shape.
        minWidth: num(props.minWidth) || undefined,
        opacity: enabled ? ((busy || submitting) ? 0.7 : 1) : 0.4,
        cursor: enabled ? (submitting ? 'default' : 'pointer') : 'not-allowed',
        transition: 'opacity 140ms var(--ease), transform 140ms var(--ease)',
      }}
    >
      {showSpinner ? <span aria-hidden style={BUTTON_SPINNER} />
        : (props.icon ? <Icon props={{ name: props.icon, size: 15, tone: 'currentColor' }} /> : null)}
      <span>{props.label}</span>
      {props.trailingIcon ? (
        <span data-trailing-icon={props.trailingIcon} style={{ display: 'inline-flex' }}>
          <Icon props={{ name: props.trailingIcon, size: 15, tone: 'currentColor' }} />
        </span>
      ) : null}
    </button>
  );
}

/** 持续请求态使用真实进度环，不能再依赖按钮短暂变暗来暗示 Session 正在创建。 */
const BUTTON_SPINNER = {
  width: 15, height: 15, borderRadius: '50%', flex: '0 0 auto',
  border: '2px solid color-mix(in srgb, currentColor 35%, transparent)',
  borderTopColor: 'currentColor',
  animation: 'viNativeSpin 0.8s linear infinite',
};

// A tiny inline glyph set — no icon-font dependency, so the kit renders the
// same in the studio, the app WebView fallback and a screenshot.
const GLYPHS = {
  warning: '⚠',
  alert: '⚠',
  camera: '◉', plus: '+', chat: '✳', clock: '◔', star: '★', trash: '⌫',
  search: '⌕', close: '✕', chevron: '›', check: '✓', warn: '!', pill: '◍',
  shirt: '◇', shoe: '◣', bag: '◗', calendar: '▤', flame: '▲',
  // "a picture", as opposed to `camera` ("take one") — the invitation an empty
  // media well wears, and the one word the set was missing for it.
  photo: '▣',
  // document-toolbox glyphs (Scanner Deck ToolGrid): geometric stand-ins for
  // the document actions; the Flutter kit draws the matching Material icons.
  scan: '⛶', id: '▭', excel: '▦', pdf: '▧', text: '≡', word: '¶', book: '❏', grid: '⊞',
  mail: '✉', send: '➤', 'external-link': '↗', 'arrow-up-right': '↗', sparkle: '✦', edit: '✎',
  alarm: '⏰', shield: '⛨', refresh: '↻', 'wifi-off': '⌀', 'mail-off': '✉',
};

export function Icon({ props = {}, emit }) {
  const size = props.size || 16;
  const style = {
    fontSize: size,
    lineHeight: 1,
    color: toneColor(props.tone),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
  };
  const tap = emit ? { role: 'button', onClick: () => emit('tapped', {}) } : { role: 'img' };
  // The legacy glyph table wins first (every shipped blueprint keeps its glyph); any other name is
  // a Lucide icon, loaded on demand so the bundle never carries the whole set.
  const legacy = GLYPHS[props.name];
  if (!legacy && isLucideIconName(props.name)) {
    return (
      <span {...tap} aria-label={props.name} style={style}>
        <DynamicIcon name={lucideIconName(props.name)} size={size} color="currentColor" strokeWidth={1.75} aria-hidden />
      </span>
    );
  }
  return (
    <span {...tap} aria-label={props.name} style={style}>
      {legacy || '•'}
    </span>
  );
}

/**
 * Monogram — a DETERMINISTIC plate + initials for a key that has no picture.
 *
 * The point is determinism: the same key always earns the same colour, so a
 * pictureless row is recognisable across a list and across sessions instead of
 * being a grey hole. Generic — it hashes whatever string it is bound to and
 * knows nothing about what that string names.
 */
export function Monogram({ variant, props = {}, data }) {
  const value = props.value !== undefined ? props.value : data;
  const text = props.text != null && props.text !== '' ? String(props.text) : initialsOf(value);
  const shape = props.shape || variant || 'square';
  const hue = MONO_HUES[hashKey(props.seed ?? value ?? text) % MONO_HUES.length];
  const tone = props.fill ? '#FFFFFF' : (props.tone ? toneColor(props.tone) : `hsl(${hue} 44% 42%)`);
  const plate = props.fill
    ? toneColor(props.fill)
    : (props.tone
      ? toneSoft(props.tone)
      : `linear-gradient(150deg, hsl(${hue} 62% 92%), hsl(${(hue + 24) % 360} 54% 84%))`);
  const size = props.size || null;

  return (
    <div
      role="img"
      aria-label={props.label || (value == null ? 'monogram' : String(value))}
      style={{
        width: size || '100%', height: size || undefined,
        aspectRatio: size ? undefined : ratioValue(props.ratio, '1 / 1'),
        borderRadius: shape === 'circle' ? '50%' : 'var(--r-sm)',
        background: plate, color: tone,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-display)', fontWeight: 800, letterSpacing: '0.02em',
        fontSize: props.fontSize || (size ? Math.min(20, Math.round(size * 0.45)) : 'var(--t-xl)'),
        overflow: 'hidden', flex: '0 0 auto', minWidth: 0,
      }}
    >
      {text}
    </div>
  );
}

/**
 * Video — inline motion, and the one thing `Image` structurally is not.
 *
 * SEALED: reduced motion NEVER autoplays (the poster stands in), a failed
 * source falls back to the poster rather than a broken box, and sound starts
 * OFF — a widget may not surprise a room with audio. `autoplay` is a policy
 * (`off | visible | idle`), not a boolean, because "play when it scrolls into
 * view" and "play immediately" are different promises.
 *
 * `autoplay: "off"` WITHOUT `controls` is a THUMBNAIL, and a thumbnail is a
 * still — so it renders the poster, never a `<video>`. A real element there
 * showed the poster for a beat and then swapped it for the clip's own first
 * frame (black on most encodes) the moment metadata loaded: the picture
 * appeared and then "broke" into a grey tile, and `onError` never fires because
 * nothing failed. A rail of off-autoplay tiles has no player to offer — the
 * play badge and tap-to-open detail are what it promises — so the poster is
 * both the honest render and the only stable one. A tap still emits: the
 * poster forwards `emit`, so the card's connection is unchanged.
 */
export function Video({ props = {}, data, emit, slots }) {
  const raw = props.src !== undefined ? props.src : data;
  const src = Array.isArray(raw) ? raw[0] : raw;
  const poster = props.poster || null;
  const [failed, setFailed] = useState(false);
  const pushedMute = slots?.setMuted?.payload;
  const muted = pushedMute?.muted !== undefined ? Boolean(pushedMute.muted) : props.muted !== false;
  const reduced = prefersReducedMotion();
  const autoplay = props.autoplay === undefined ? 'visible' : props.autoplay;

  useEffect(() => { setFailed(false); }, [src]);

  /** 有有效片源时，长按始终描述它所代表的视频，而不是当前画着的海报。 */
  const videoLongPress = src && emit
    ? longPressHandlers(() => emit('longPressed', { src, video_url: src }))
    : {};

  // No source, a broken one, a reader who asked for no motion, or a THUMBNAIL
  // (`autoplay: "off"` with nothing to press): the POSTER is the honest render,
  // and it is also this component's declared fallback. `controls` opts back in
  // — a deliberate tap-to-play player stays a real `<video>` at any autoplay.
  if (!src || failed || ((reduced || autoplay === 'off') && !props.controls)) {
    if (!poster) return null;
    const still = <Image props={{ src: poster, fit: props.fit, ratio: props.ratio, rounded: props.rounded }} emit={emit} />;
    // 片源存在但播放降级时，海报仍代表同一条视频，长按保存能力不能随动效一起消失。
    return src ? <div {...videoLongPress} style={{ width: '100%' }}>{still}</div> : still;
  }

  return (
    <video
      src={src}
      poster={poster || undefined}
      loop={props.loop !== false}
      muted={muted}
      playsInline
      controls={Boolean(props.controls)}
      autoPlay={autoplay !== 'off' && !reduced}
      preload={autoplay === 'off' ? 'metadata' : 'auto'}
      aria-label={props.label || 'video'}
      {...videoLongPress}
      onLoadedData={() => emit?.('ready', { src })}
      onError={() => { setFailed(true); emit?.('failed', { src }); }}
      onClick={emit ? () => emit('tapped', { src }) : undefined}
      style={{
        width: '100%', display: 'block',
        aspectRatio: ratioValue(props.ratio, undefined),
        objectFit: props.fit === 'contain' ? 'contain' : 'cover',
        borderRadius: props.rounded === false ? 0 : 'var(--r-sm)',
        background: 'var(--surface-3)',
      }}
    />
  );
}

/** `fallbackShape` → the corner a drawn fallback takes. */
const FALLBACK_RADIUS = { square: '0', rounded: 'var(--r-sm)', circle: '50%' };

/**
 * THE FALLBACK VOCABULARY — what a picture draws when there is no picture.
 *
 * There used to be two answers: a deterministic `monogram` plate, and a `◍`
 * glyph for everything else. That glyph is the same mark whether the missing
 * thing is a scanned page, a pill bottle, a garment cut-out or a book — so
 * every pictureless grid in the app read as the same grey hole, and five
 * blueprints each reached for a different hack to avoid it.
 *
 * The vocabulary is closed and NAMES WHAT IS MISSING, never how to draw it:
 *   `monogram`    a deterministic plate + initials — "no photo, but THIS row"
 *   `document`    a ruled sheet — a scan/page that has not resolved
 *   `plate`       a flat tone panel and nothing else — the quietest stand-in,
 *                 for a tile whose caption already says what it is
 *   `silhouette`  a filled SHAPE (`fallbackShape`) — a cut-out degrades to an
 *                 outline, which is the one honest reading of "an object, no
 *                 picture of it"
 *   `title`       the `fallbackValue` set large on a plate — for the thing
 *                 whose NAME is all we have (a book spine, a document)
 *
 * `fallbackColor` overrides the plate (a tone name or any CSS colour) and
 * `fallbackShape` its corner. Absent a declared kind this is the `◍` it always
 * was, so nothing that did not ask for the vocabulary moves.
 */
export function ImageFallback({ kind, value, color, shape, tone, alt }) {
  const plate = color ? toneColor(color) : (tone ? toneSoft(tone) : 'var(--surface-3)');
  const ink = tone ? toneColor(tone) : 'var(--text-3)';
  const radius = FALLBACK_RADIUS[shape] || (shape === undefined ? '0' : '0');
  const box = {
    position: 'absolute', inset: 0, display: 'flex',
    alignItems: 'center', justifyContent: 'center', background: color || tone ? plate : 'transparent',
  };

  if (kind === 'document') {
    return (
      <div style={box} role="img" aria-label={alt || 'document'}>
        <div style={{
          width: '54%', maxWidth: 72, background: 'var(--surface)', borderRadius: 'var(--r-sm)',
          border: '1px solid var(--line)', padding: '10% 12%',
          display: 'flex', flexDirection: 'column', gap: '14%',
        }}>
          {[80, 100, 62].map((w) => (
            <span key={w} aria-hidden style={{ display: 'block', height: 2, width: `${w}%`, borderRadius: 1, background: ink, opacity: 0.45 }} />
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'silhouette') {
    return (
      <div style={box} role="img" aria-label={alt || 'no picture'}>
        <span aria-hidden style={{
          width: '46%', height: '46%', background: ink, opacity: 0.28,
          borderRadius: shape === 'circle' ? '50%' : (shape === 'square' ? '0' : 'var(--r-sm)'),
        }} />
      </div>
    );
  }

  if (kind === 'title') {
    const label = String(value ?? alt ?? '').trim();
    return (
      <div style={{ ...box, padding: 'var(--sp-3)', background: color ? plate : 'var(--surface-2)' }} role="img" aria-label={alt || label || 'untitled'}>
        <span style={{
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--t-md)',
          lineHeight: 1.15, letterSpacing: '-0.01em', color: tone ? ink : 'var(--text-2)',
          textAlign: 'center', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', wordBreak: 'break-word',
        }}>{label}</span>
      </div>
    );
  }

  if (kind === 'plate') {
    return (
      <div
        role="img"
        aria-label={alt || 'no picture'}
        style={{ ...box, background: plate, borderRadius: radius }}
      />
    );
  }

  return (
    <div style={{ ...box, color: ink, fontSize: 22 }} aria-hidden>◍</div>
  );
}

/**
 * SEALED: media:// never reaches the DOM — the host resolves it to a signed /
 * proxied URL before the feed lands. A shimmer plate while loading, silhouette
 * on a broken URL (a wardrobe cutout must degrade to a shape, never a broken
 * icon).
 *
 * `fallback` names WHAT IS MISSING from the closed vocabulary above
 * (`monogram` · `document` · `plate` · `silhouette` · `title`); `fallbackKind`
 * is the same prop under the name a blueprint that also carries `fallbackValue`
 * / `fallbackColor` / `fallbackShape` naturally reaches for, and it wins.
 *
 * THREE FRAMING props, all about a picture that is not a neat square:
 *   `position`  the crop anchor (`"top center"`). A scanned page cropped from
 *               the middle loses its heading, which is the half a user reads.
 *   `inset`     a % of padding INSIDE the frame, so a cut-out breathes instead
 *               of touching its own edge.
 *   `lift`      a drop shadow under the object — what makes a `contain`-fit
 *               cut-out read as an object ON the page rather than a sticker
 *               printed into it.
 */
export function Image({ variant, props = {}, data, emit }) {
  const raw = props.src !== undefined ? props.src : data;
  const src = Array.isArray(raw) ? raw[0] : raw;
  const [state, setState] = useState(src ? 'loading' : 'failed');
  const fit = props.fit || variant || 'cover';
  const kind = props.fallbackKind || props.fallback || null;
  const inset = num(props.inset);
  // SHAPE is the real-media clip (default = the existing `rounded` corner):
  // `circle` clips a real `src` — not just the fallback monogram — to a circle,
  // which is what a row of round thumbnails needs; `square`/`rounded` name the
  // two corner readings explicitly. The frame is `overflow: hidden`, so the
  // radius clips the picture and every fallback alike.
  const shape = props.shape || null;
  const frameRadius = shape === 'circle' ? '50%'
    : shape === 'square' ? 0
      : shape === 'rounded' ? 'var(--r-sm)'
        : (props.rounded === false ? 0 : 'var(--r-sm)');

  useEffect(() => { setState(src ? 'loading' : 'failed'); }, [src]);

  const frame = {
    position: 'relative',
    width: '100%',
    aspectRatio: ratioValue(props.ratio, undefined),
    borderRadius: frameRadius,
    overflow: 'hidden',
    // `plate: false` puts the object straight on the page — what a chrome-less
    // cut-out needs, and the one thing a fixed backdrop would ruin.
    background: props.plate === false ? 'transparent' : 'var(--surface-3)',
    display: 'block',
  };

  if (String(src || '').startsWith('media://') || state === 'failed') {
    if (kind === 'monogram') {
      // the initials scale with the plate, so a 32px avatar is not a 20px letter in a coin
      return (
        <div style={{ ...frame, containerType: 'inline-size' }}>
          <Monogram props={{
            value: props.fallbackValue ?? props.alt ?? src,
            tone: props.fallbackTone,
            shape: props.fallbackShape === 'circle' ? 'circle' : undefined,
            ratio: props.ratio,
            label: props.alt,
            fontSize: 'min(var(--t-xl), 40cqw)',
          }} />
        </div>
      );
    }
    return (
      <div style={frame} aria-label={props.alt || 'image unavailable'}>
        <ImageFallback
          kind={kind}
          value={props.fallbackValue ?? props.alt ?? null}
          color={props.fallbackColor}
          shape={props.fallbackShape}
          tone={props.fallbackTone}
          alt={props.alt}
        />
      </div>
    );
  }

  return (
    <div style={{
      ...frame,
      // `inset` is padding INSIDE the frame; the picture then fills what is
      // left, so the frame's own box (and the grid row it sits in) is unmoved.
      padding: inset ? `${inset}%` : undefined,
      boxSizing: inset ? 'border-box' : undefined,
    }}>
      {state === 'loading' && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(100deg, var(--surface-3) 30%, var(--surface-2) 50%, var(--surface-3) 70%)',
          backgroundSize: '200% 100%', animation: 'viNativeShimmer 1.2s linear infinite',
        }} />
      )}
      <img
        // A data: URI or an already-cached image is often `complete` before
        // React binds onLoad, so the load event never fires, `state` stays
        // `loading`, and the fade-in below sticks at opacity 0 — the poster
        // covers (inline webp) rendered fully decoded but invisible. Catch the
        // already-decoded case on ref; onLoad still covers the async network path.
        ref={(node) => { if (node && node.complete && node.naturalWidth > 0) setState('loaded'); }}
        src={src}
        alt={props.alt || ''}
        // EAGER, deliberately. Native lazy-loading needs an intersection the
        // browser can compute at first paint, and the studio renders this page
        // inside a `transform: scale()` phone frame nested in its own scroller:
        // the observer never fires there, so the picture stayed grey until some
        // unrelated round-trip forced a reflow — and the scaled focus-zone copy,
        // which never reflows, stayed grey forever. A widget page carries a
        // handful of images, so paying for all of them beats a blank phone.
        loading="eager"
        onLoad={() => setState('loaded')}
        onError={() => setState('failed')}
        onClick={emit ? () => emit('tapped', { src, index: props.index ?? 0 }) : undefined}
        style={{
          width: '100%', height: '100%', objectFit: fit === 'contain' ? 'contain' : 'cover',
          // WHERE the crop bites. `cover` throws away the overflow, and the
          // default is the middle — so a scanned page loses its heading, which
          // is the half a user actually reads.
          objectPosition: props.position || undefined,
          display: 'block', opacity: state === 'loaded' ? 1 : 0,
          transition: 'opacity 220ms var(--ease)',
          cursor: emit ? 'zoom-in' : 'default',
          filter: props.lift ? 'drop-shadow(0 6px 12px rgba(0,0,0,0.28))' : undefined,
        }}
      />
    </div>
  );
}

/**
 * Badge — a tone-coded pill or dot. SEALED: the tone NAMES a status colour
 * (good/warn/bad/accent/text-3), it never picks one. A card binds both the
 * tone and the text from its row, so the badge itself carries no rule about
 * what makes a row good or bad — that lives in a derive.
 */
export function Badge({ variant = 'pill', props = {} }) {
  const tone = props.tone || 'text-3';
  const text = props.text == null ? '' : String(props.text);
  const dot = props.dot === true || variant === 'dot';
  if (!text && !dot && !props.icon) return null;

  if (dot && !text) {
    return (
      <span
        role="img"
        aria-label={props.label || tone}
        style={{
          width: 10, height: 10, borderRadius: 5, flex: '0 0 auto',
          background: toneColor(tone), boxShadow: '0 0 0 2px var(--surface)',
        }}
      />
    );
  }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, flex: '0 0 auto',
      padding: '2px 8px', borderRadius: 'var(--r-pill)',
      background: toneSoft(tone), color: toneColor(tone),
      fontSize: 'var(--t-xs)', fontWeight: 700, letterSpacing: '0.01em',
      maxWidth: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
    }}>
      {dot ? (
        <span aria-hidden style={{ width: 6, height: 6, borderRadius: 3, background: toneColor(tone) }} />
      ) : null}
      {props.icon ? <Icon props={{ name: props.icon, size: 11, tone }} /> : null}
      {text}
    </span>
  );
}

/**
 * `justify` is the MAIN axis — the one a gap cannot express: a label and its
 * value pushed to the two ends of a row is `space-between`, not a spacer child
 * and not a guessed padding. Written out rather than passed through so both
 * runtimes read the same six words (Flutter's `MainAxisAlignment` twin).
 */
const JUSTIFY = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  // Both spellings: the registry schema documents the short one, the kit grew
  // up on the CSS one, and a blueprint that picked either silently got neither.
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
  'space-between': 'space-between',
  'space-around': 'space-around',
  'space-evenly': 'space-evenly',
};

/**
 * SEALED: safe area + no absolute layout. Flex only.
 *
 * `Stack` is now the OVERLAP strip and nothing else: the plates SIT ON each
 * other, which is how a row of people (or covers, or versions) says "these
 * several, as one thing" in the width of about two. It stays flex — a negative
 * gap, not a coordinate — so the seal holds. Linear layout moved to the
 * explicit `Vertical` / `Horizontal` primitives (Qt's VBox / HBox); layered
 * placement is `Overlay`.
 *
 * It caps at `max` and grows an auto `+N` TAIL rather than running off the row:
 * an overlap strip's job is to be glanceable, and a strip whose width tracks
 * the collection size is a strip that stops being one at eleven.
 *
 * BACKWARD-COMPAT (#4527): before #4527 `Stack` was the linear-layout superset,
 * selecting axis with `direction:"row"|"column"`. #4527 narrowed it to the overlap
 * strip and moved linear to `Vertical`/`Horizontal`, which broke already-published,
 * store-pinned blueprints (e.g. doc-digitizer's 19 explicit-`direction` Stacks
 * collapse into tiny overlap piles). So `Stack` is a superset again: `direction:"row"`
 * → horizontal linear, `direction:"column"` → vertical linear, `direction:"overlap"`
 * or unset → the overlap strip below. `Vertical`/`Horizontal` remain the preferred
 * explicit spelling; `direction` is honored only for compatibility.
 */
export function Stack({ props = {}, children }) {
  if (props.direction === 'row') return <Linear row props={props}>{children}</Linear>;
  if (props.direction === 'column') return <Linear props={props}>{children}</Linear>;
  const kids = Children.toArray(children);
  const max = Number(props.max) > 0 ? Number(props.max) : 4;
  const shown = kids.slice(0, max);
  const rest = kids.length - shown.length;
  const overlap = props.overlap === undefined ? -6 : Number(props.overlap) || 0;
  const size = num(props.size);
  const plate = size ? { width: size, height: size, flex: '0 0 auto' } : { flex: '0 0 auto' };
  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minWidth: 0 }}>
      {shown.map((child, i) => (
        <div key={i} style={{ ...plate, marginLeft: i === 0 ? 0 : overlap, zIndex: shown.length - i }}>
          {child}
        </div>
      ))}
      {rest > 0 ? (
        <div style={{
          ...plate, marginLeft: overlap, borderRadius: 'var(--r-pill)',
          minWidth: size || 26, height: size || 26, padding: size ? 0 : '0 8px',
          background: 'var(--surface-3)', color: 'var(--text-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'var(--t-xs)', fontWeight: 700,
        }}>{`+${rest}`}</div>
      ) : null}
    </div>
  );
}

/**
 * `Vertical` / `Horizontal` — the linear layout spine (Qt's VBox / HBox), flex only.
 *
 * STANDARDIZED stretch (README "Layout & stretch"): `align:"stretch"` fills the cross
 * axis — a column's cards go equal-WIDTH, a row's equal-HEIGHT — the same force as
 * Grid's per-row equal height and a Card filling a stretched cell. CSS's own default
 * (column → stretch of AUTO-sized children, row → center) is preserved here; a
 * sized child keeps its size. `align:"start|center|end|stretch"` sets the cross axis;
 * `justify` places the main axis; `gap` spaces; `Horizontal` also honors `wrap`.
 */
function Linear({ row, props = {}, children }) {
  const boxStyle = {
    display: 'flex',
    flexDirection: row ? 'row' : 'column',
    gap: gapValue(props.gap),
    alignItems: props.align || (row ? 'center' : 'stretch'),
    justifyContent: JUSTIFY[props.justify] || 'flex-start',
    flexWrap: row && props.wrap ? 'wrap' : 'nowrap',
    minWidth: 0,
  };

  // `divided` — a hairline BETWEEN adjacent children (a ledger look), column
  // only: nothing above the first or below the last. Each child past the first
  // wears a `borderTop` in the subtle line tone; the existing `gap` still spaces
  // the rows. A row ignores it (a vertical rule between row items is a different
  // component). Opt-in, so an undivided Vertical is byte-identical. Flutter twin:
  // `Divider(height: gap, thickness: 0.5)` centered in the gap between children.
  if (props.divided && !row) {
    const kids = Children.toArray(children);
    return (
      <div style={boxStyle}>
        {kids.map((child, i) => (
          <div
            key={i}
            style={i === 0 ? { minWidth: 0 } : { minWidth: 0, borderTop: '0.5px solid var(--line-soft)' }}
          >
            {child}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={boxStyle}>
      {children}
    </div>
  );
}

export function Vertical({ props = {}, children }) {
  return <Linear row={false} props={props}>{children}</Linear>;
}

export function Horizontal({ props = {}, children }) {
  return <Linear row props={props}>{children}</Linear>;
}

/** 局部留白不带背景，四边按逻辑像素读取，保持与原生一致。 */
export function Padding({ props = {}, children, childItems }) {
  const inset = (side) => Number.isFinite(Number(props[side])) ? Math.max(0, Number(props[side])) : 0;
  if (childItems?.length && childItems.every((child) => child.hidden)) return null;
  return <div className="vi-native-padding" style={{ minWidth: 0, boxSizing: 'border-box',
    padding: `${inset('top')}px ${inset('right')}px ${inset('bottom')}px ${inset('left')}px` }}>{children}</div>;
}

/**
 * The nine placements: five edges, the four CORNERS, and fill.
 *
 * `alignItems` is the cross (vertical) axis, `justifyContent` the main
 * (horizontal) one. The corners exist because the commonest overlay there is —
 * a badge pinned to the top-right of a thumbnail — was not expressible at all:
 * `top` centred it and `end` middled it, so authors reached for a Stack and
 * lost the layering.
 */
const OVERLAY_REGION = {
  center: { alignItems: 'center', justifyContent: 'center' },
  top: { alignItems: 'flex-start', justifyContent: 'center' },
  bottom: { alignItems: 'flex-end', justifyContent: 'center' },
  start: { alignItems: 'center', justifyContent: 'flex-start' },
  end: { alignItems: 'center', justifyContent: 'flex-end' },
  'top-start': { alignItems: 'flex-start', justifyContent: 'flex-start' },
  'top-end': { alignItems: 'flex-start', justifyContent: 'flex-end' },
  'bottom-start': { alignItems: 'flex-end', justifyContent: 'flex-start' },
  'bottom-end': { alignItems: 'flex-end', justifyContent: 'flex-end' },
  fill: { alignItems: 'stretch', justifyContent: 'stretch' },
};

/**
 * Overlay — the Z-layered container, and the ONE sanctioned exception to
 * Stack's "no absolute layout" seal.
 *
 * The FIRST child is the BASE and lays out in normal flow, so it (not a magic
 * height) defines the box. Every later child is painted OVER it, placed by its
 * own `region` (center · top · bottom · start · end · the four corners · fill;
 * default center). That is the whole grammar — no coordinates, no z-index, no
 * escape from the base's box, which is what keeps "absolute" safe here: an
 * overlay can never paint outside the thing it is over, and can never cover the
 * safe area.
 *
 * A `when`-FALSE child is dropped before layering rather than layered as an
 * empty box: a gated badge otherwise left a full-size transparent layer over
 * the base for every gate that did not fire, and — when the gated child was
 * FIRST — an empty base, i.e. an overlay with no box at all.
 *
 * This is what a ring numeral, an image caption or a badge-on-thumbnail needs
 * and Stack (flex only) structurally cannot express.
 */
export function Overlay({ props = {}, childItems = [], children }) {
  const layers = childItems.length
    ? childItems.filter((c) => !c.hidden)
    : Children.toArray(children).map((element, i) => ({ id: `layer${i}`, region: null, element }));
  if (!layers.length) return null;
  const [base, ...over] = layers;

  return (
    <div style={{
      position: 'relative', display: 'flex', minWidth: 0, alignSelf: props.align || 'center',
      width: props.width || 'fit-content', maxWidth: '100%',
    }}>
      <div style={{ display: 'flex', minWidth: 0, width: '100%' }}>{base.element}</div>
      {over.map((layer) => (
        <div
          key={layer.id}
          style={{
            position: 'absolute', inset: 0, display: 'flex', minWidth: 0,
            // The layer itself is transparent to taps; only what it draws is
            // clickable, so an overlay never steals the base's gestures.
            pointerEvents: 'none',
            ...(OVERLAY_REGION[layer.region] || OVERLAY_REGION.center),
          }}
        >
          <div style={{ pointerEvents: 'auto', minWidth: 0, maxWidth: '100%' }}>{layer.element}</div>
        </div>
      ))}
    </div>
  );
}

/** SEALED: repeat(N, minmax(0,1fr)) — a grid NEVER overflows horizontally. */
export function Grid({ props = {}, children }) {
  const columns = Number(props.columns) || 2;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gap: gapValue(props.gap, 'var(--sp-2)'),
      minWidth: 0,
    }}>
      {children}
    </div>
  );
}

const SURFACE_VARIANT = {
  plain: { background: 'transparent' },
  card: { background: 'var(--surface)', boxShadow: 'var(--shadow-1)' },
  recessed: { background: 'var(--surface-2)' },
  well: { background: 'var(--surface-3)' },
  'accent-rail': { background: 'var(--surface)', boxShadow: 'var(--shadow-1)' },
  outlined: { background: 'var(--surface)', border: '1px solid var(--line)' },
};

/**
 * `accent-rail` is the card with a TONE BAR down its leading edge.
 *
 * It is the cheapest way to say "this panel is about that": the rail carries a
 * status colour the panel's content does not have to spend a badge on, and a
 * column of panels reads as a colour-coded list at a glance rather than as a
 * stack of identical white boxes. The bar is 5px, full height, and takes the
 * surface's own corner on the leading side — anything thinner reads as a
 * rendering artefact.
 */
export function Surface({ variant = 'card', props = {}, children, emit, connected }) {
  const plain = variant === 'plain';
  // a wired `activated` makes the whole plate one tap target (app 0.10.2+)
  const tap = emit && connected?.('activated')
    ? { role: 'button', tabIndex: 0, onClick: () => emit('activated', {}), style: { cursor: 'pointer' } }
    : null;
  const rail = variant === 'accent-rail' || props.variant === 'accent-rail';
  const box = {
    ...(variant === 'tinted' ? { background: toneSoft(props.tone || 'accent') } : null),
    ...SURFACE_VARIANT[rail ? 'accent-rail' : variant] || (variant === 'tinted' ? null : SURFACE_VARIANT.card),
    borderRadius: props.radius != null ? gapValue(props.radius) : (plain ? 0 : 'var(--r)'),
    // A hairline is the only chrome a recessed plate can carry: it separates
    // the plate from a page of the same family of greys.
    ...(props.bordered ? { border: '1px solid var(--line)' } : {}),
    // `appearance` flips the TOKENS for the subtree, so a dark panel's ink,
    // lines and nested plates invert with it instead of being painted white.
    ...(props.appearance === 'dark' ? buildTokenStyle({ appearance: 'dark' }) : {}),
    ...(Array.isArray(props.gradient) && props.gradient.length > 1
      ? { backgroundImage: `linear-gradient(135deg, ${props.gradient[0]}, ${props.gradient[1]})` }
      : {}),
    // 背景属于容器，内部 Padding 只移动内容，图片失败仍保留底色。
    // a tone NAME (`accent`, `surface`) resolves to the token; a CSS colour passes through
    ...(props.backgroundColor ? { backgroundColor: toneColor(props.backgroundColor) } : {}),
    ...(props.backgroundImage && !String(props.backgroundImage).startsWith('media://') ? {
      backgroundImage: `url(${JSON.stringify(props.backgroundImage)})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
    } : {}),
    minWidth: 0,
  };

  if (rail) {
    return (
      <div style={{ ...box, display: 'flex', overflow: 'hidden' }}>
        <span aria-hidden style={{
          flex: '0 0 5px', width: 5, alignSelf: 'stretch',
          background: toneColor(props.tone || 'accent'),
        }} />
        <div style={{ flex: 1, minWidth: 0, padding: gapValue(props.padding, 'var(--sp-4)') }}>{children}</div>
      </div>
    );
  }

  return (
    <div
      role={tap?.role}
      tabIndex={tap?.tabIndex}
      onClick={tap?.onClick}
      style={{
        ...box, ...tap?.style,
        ...(variant === 'outlined' ? { overflow: 'hidden' } : null),
        padding: gapValue(props.padding, plain ? 0 : 'var(--sp-4)'),
      }}
    >
      {children}
    </div>
  );
}

/**
 * CaptureButton — registry `render: "spec"`. The HOST owns the '+' capture button; the
 * widget only declares the intent. The preview draws the host affordance
 * dashed so the page composition is legible without faking host chrome.
 */
export function CaptureButton({ props = {} }) {
  return <SpecOnly name="CaptureButton" note={`host draws '+' → ${props.action || 'open_capture'}`} />;
}

/**
 * Divider — a horizontal rule, the generic separator a composition reaches for
 * between two stacked regions. `variant: "line"` (default) is a solid hairline;
 * `variant: "dashed"` is the teal dashed rule the chart draws under its header,
 * so a card that stacks a chart over a thumbnail rail can echo it.
 */
export function Divider({ variant = 'line', props = {} }) {
  const dashed = (props.variant || variant) === 'dashed';
  return (
    <div
      role="separator"
      style={{
        height: 0,
        width: '100%',
        borderTop: dashed ? '1px dashed var(--accent-line)' : '1px solid var(--line)',
      }}
    />
  );
}
